import { useTranslation } from "react-i18next";

import {
  ActionButton,
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  CardSkeleton,
  CardTitle,
  IconBadge,
  typography,
} from "@dsaui/components";
import { cn } from "@dsaui/utils";
import { CreditCardOutline } from "@dsaui/icons";

export interface MembershipCardProps {
  billingName: string;
  billingDetails: string[];
  duesDetail: string;
  cardExpiration: string | null;
  loading?: boolean;
  nextPaymentDate: string | null;
  paymentMethod: string | null;
  updateHref: string;
}

export const MembershipCard = ({
  billingName,
  billingDetails,
  duesDetail,
  cardExpiration,
  loading = false,
  nextPaymentDate,
  paymentMethod,
  updateHref,
}: MembershipCardProps) => {
  const { t } = useTranslation();
  const metricLabelClassName = cn(
    typography.profileDetail.className,
    "min-w-0 text-right font-semibold text-gray-900",
  );
  const metricValueClassName = cn(
    typography.profileDetail.className,
    "min-w-0 wrap-anywhere",
  );

  if (loading) return <CardSkeleton className="h-full" rows={6} showBanner={false} />;

  return (
    <Card className="h-full">
      <CardBody className="h-full">
        <CardHeader className="flex-row items-center">
          <IconBadge>
            <CreditCardOutline className="size-6" />
          </IconBadge>
          <CardTitle>{t("dashboard.membership.title")}</CardTitle>
        </CardHeader>

        <dl className="grid grid-cols-[max-content_minmax(0,1fr)] gap-x-4 gap-y-4">
          <dt className={metricLabelClassName}>
            {t("dashboard.membership.billingName")}
          </dt>
          <dd className={metricValueClassName}>{billingName}</dd>

          <dt className={metricLabelClassName}>
            {t("dashboard.membership.billingAddress")}
          </dt>
          <dd className={metricValueClassName}>
            {billingDetails.map((line) => (
              <div key={line}>{line}</div>
            ))}
          </dd>

          <dt className={metricLabelClassName}>{t("dashboard.membership.dues")}</dt>
          <dd className={cn("m-0", metricValueClassName)}>{duesDetail}</dd>

          <dt className={metricLabelClassName}>
            {t("dashboard.membership.paymentMethod")}
          </dt>
          <dd className={cn("m-0", metricValueClassName, "whitespace-pre-line")}>
            {paymentMethod ?? t("dashboard.personalInformation.missingValue")}
          </dd>

          {cardExpiration && (
            <>
              <dt className={metricLabelClassName}>
                {t("dashboard.membership.cardExpiration")}
              </dt>
              <dd className={cn("m-0", metricValueClassName)}>
                {cardExpiration ?? t("dashboard.personalInformation.missingValue")}
              </dd>
            </>
          )}

          <dt className={metricLabelClassName}>
            {t("dashboard.membership.nextPayment")}
          </dt>
          <dd className={cn("m-0", metricValueClassName)}>
            {nextPaymentDate ?? t("dashboard.personalInformation.missingValue")}
          </dd>
        </dl>

        <CardFooter>
          <ActionButton href={updateHref}>
            {t("dashboard.membership.actions.viewDetails")}
          </ActionButton>
        </CardFooter>
      </CardBody>
    </Card>
  );
};

export default MembershipCard;
